import {
  type CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import { COUNTRY_NAMES } from "../constants/country-names";

/**
 * Parses a phone string (E.164 like +91… or national digits) and returns display
 * parts for marketing UI. Uses `defaultCountry` when the number has no country prefix.
 */
export function parsePhoneForMarketingDisplay(
  phoneNumber: string,
  options?: { defaultCountry?: CountryCode }
): {
  /** ISO 3166-1 alpha-2 for flag assets */
  iso: string;
  /** e.g. "+91" */
  callingCodeLabel: string;
  nationalNumber: string;
} {
  const defaultCountry: CountryCode = options?.defaultCountry ?? "IN";
  const defaultCalling = `+${getCountryCallingCode(defaultCountry)}`;
  const trimmed = phoneNumber?.trim() ?? "";
  if (!trimmed) {
    return {
      iso: defaultCountry,
      callingCodeLabel: defaultCalling,
      nationalNumber: "",
    };
  }

  const parsed = parsePhoneNumberFromString(trimmed, defaultCountry);
  if (!parsed) {
    const digits = trimmed.replace(/\D/g, "");
    return {
      iso: defaultCountry,
      callingCodeLabel: defaultCalling,
      nationalNumber: digits || trimmed,
    };
  }

  const iso = parsed.country ?? defaultCountry;
  return {
    iso,
    callingCodeLabel: `+${parsed.countryCallingCode}`,
    nationalNumber: parsed.nationalNumber,
  };
}

export type CallingCodeOption = {
  iso: CountryCode;
  name: string;
  callingCode: string;
};

let callingCodeOptions: CallingCodeOption[] | null = null;

/** All countries with their calling codes, sorted by name. */
export function getCallingCodeOptions(): CallingCodeOption[] {
  if (callingCodeOptions) return callingCodeOptions;
  callingCodeOptions = getCountries()
    .map((iso) => ({
      iso,
      name: COUNTRY_NAMES[iso] ?? iso,
      callingCode: `+${getCountryCallingCode(iso)}`,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return callingCodeOptions;
}

export function getFlagImageUrl(iso: string) {
  return `https://flagcdn.com/w40/${iso.toLowerCase()}.png`;
}
